import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const useRailNextBatchTrigger = ({
  rootRef,
  onReach,
  isEnabled,
}: {
  rootRef: RefObject<HTMLElement>;
  onReach: () => void;
  isEnabled: boolean;
}): ((node: HTMLElement | null) => void) => {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const onReachRef = useRef(onReach);
  const isEnabledRef = useRef(isEnabled);
  const sentinelNodeRef = useRef<HTMLElement | null>(null);
  const isIntersectingRef = useRef(false);

  useLayoutEffect(() => {
    onReachRef.current = onReach;
    isEnabledRef.current = isEnabled;
  });

  // Refs attach during commit, before the isEnabled effect below reads the flag, so a batch that
  // lands and moves the sentinel in one commit never re-fires for the card it moved away from.
  const attachSentinel = useCallback((node: HTMLElement | null) => {
    sentinelNodeRef.current = node;
    isIntersectingRef.current = false;
    setSentinel(node);
  }, []);

  useEffect(() => {
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (sentinelNodeRef.current !== sentinel) return;

        isIntersectingRef.current = Boolean(entry?.isIntersecting);
        if (isIntersectingRef.current && isEnabledRef.current) onReachRef.current();
      },
      { root: rootRef.current, threshold: 0 }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sentinel, rootRef]);

  useEffect(() => {
    if (isEnabled && isIntersectingRef.current) onReachRef.current();
  }, [isEnabled]);

  return attachSentinel;
};
